import React, { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import Icon from '../ui/Icon';
import './ThemeToggle.css';

const THEME_KEY = 'theme-preference';

type Theme = 'light' | 'dark';

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

function readStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null;
  }
}

// Mirrors public/theme-init.js: a saved choice wins, otherwise follow the operating system.
export function resolveInitialTheme(): Theme {
  const stored = readStoredTheme();
  if (stored) return stored;
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

const ThemeToggle: React.FC = () => {
  const [theme, setTheme] = useState<Theme>(resolveInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow live operating-system changes until the visitor picks a theme explicitly.
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!query?.addEventListener) return;
    const onChange = (e: MediaQueryListEvent) => {
      if (!readStoredTheme()) setTheme(e.matches ? 'light' : 'dark');
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Storage blocked: the choice applies for this visit only.
    }
  };

  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      className="theme-toggle"
      onClick={toggle}
      aria-label={`Activate ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
    >
      <Icon icon={theme === 'dark' ? Sun : Moon} size={18} />
    </button>
  );
};

export default ThemeToggle;
